import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, MapPin, Users, Ticket, CalendarHeart } from 'lucide-react';
import { listEvents, EventRow, money, fmtEventDate } from '../api';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';
import EventForm from '../components/EventForm';
import { readCache, writeCache } from '../../../services/localCache';
import { useAuth } from '../../../context/AuthContext';

function EventCard({ ev }: { ev: EventRow }) {
  const color = ev.event_type_color ? `#${ev.event_type_color}` : null;
  const isPast = ev.datetime ? new Date(ev.datetime) < new Date() : false;
  return (
    <Link to={`/eventos/${ev.event_id}`}
      className="group overflow-hidden rounded-2xl border border-line bg-surface shadow-sm transition hover:shadow-md">
      {/* Flyer o degradado de marca */}
      <div className="relative h-40 w-full overflow-hidden bg-surface-2">
        {ev.image_url ? (
          <img src={ev.image_url} alt="" className="h-full w-full object-cover transition group-hover:scale-105"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
        ) : (
          <div className="h-full w-full" style={{ background: color ? `linear-gradient(135deg, ${color}, rgb(var(--c-blue)))` : 'linear-gradient(135deg, rgb(var(--c-accent)), rgb(var(--c-blue)))' }} />
        )}
        {ev.event_type && (
          <span className="absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white shadow"
            style={{ background: color || 'rgb(var(--c-blue))' }}>{ev.event_type}</span>
        )}
        {isPast && <span className="absolute right-3 top-3 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white">pasado</span>}
      </div>
      <div className="p-4">
        <h3 className="truncate font-display text-lg font-bold text-content">{ev.name}</h3>
        <p className="mt-0.5 text-sm capitalize text-content-2">{fmtEventDate(ev.datetime)}</p>
        {ev.venue && <p className="mt-1 flex items-center gap-1 text-xs text-content-3"><MapPin size={12} /> {ev.venue}</p>}
        {(Number(ev.is_reference) === 1 || Number(ev.budget_lines || 0) > 0) && (
          <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
            {Number(ev.is_reference) === 1 && <span className="rounded-full bg-surface-2 px-2 py-0.5 font-medium text-content-3">Antecedente</span>}
            {Number(ev.budget_lines || 0) > 0 && (
              <span className={`rounded-full px-2 py-0.5 font-semibold ${Number(ev.budget_profit) >= 0 ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-500'}`}>
                Utilidad {Number(ev.is_reference) === 1 ? '' : 'presupuestada '}{money(ev.budget_profit || 0)}
              </span>
            )}
          </div>
        )}
        <div className="mt-3 flex items-center justify-between text-sm">
          <span className="inline-flex items-center gap-1 text-content-2"><Users size={14} /> {ev.going ?? 0}{ev.capacity ? `/${ev.capacity}` : ''}</span>
          {ev.price != null && <span className="inline-flex items-center gap-1 font-semibold text-content"><Ticket size={14} className="text-brand-blue" /> {money(ev.price)}</span>}
        </div>
      </div>
    </Link>
  );
}

export default function EventsListPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Pinta al instante lo último conocido (caché local) y refresca por detrás.
  const [events, setEvents] = useState<EventRow[] | null>(() => readCache<EventRow[]>('events-list') ?? null);
  const [showForm, setShowForm] = useState(false);

  const load = () => listEvents().then((r) => { setEvents(r.data); writeCache('events-list', r.data); })
    .catch(() => setEvents((prev) => prev ?? []));
  useEffect(() => { load(); }, []);

  return (
    // Ruta densa (1600px) — el ancho lo gobierna AppLayout, sin tope interno.
    <div>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-content">
            <CalendarHeart className="text-brand-blue" size={24} /> Eventos
          </h1>
          <p className="mt-0.5 text-sm text-content-3">Recitales, festivales y shows: boletos y las finanzas de cada evento.</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-on-accent hover:opacity-90">
            <Plus size={16} /> Nuevo evento
          </button>
        )}
      </div>

      {!events ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}
        </div>
      ) : events.length === 0 ? (
        <EmptyState icon={CalendarHeart} title="Aún no hay eventos" subtitle="Crea el primero con el botón de arriba."
          action={isAdmin ? <button onClick={() => setShowForm(true)} className="rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-on-accent">Nuevo evento</button> : undefined} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {events.map((ev) => <EventCard key={ev.event_id} ev={ev} />)}
        </div>
      )}

      {showForm && <EventForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}
